// Configure Page - Dynamic Interview Flow
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, ArrowLeft, Loader2, Rocket, Lightbulb, Building2, Flame, LogOut, Briefcase } from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';
import UsageBanner from '../components/UsageBanner';
import useConfigStore from '../store/configStore';
import { useAuth } from '../contexts/AuthContext';
import { generateStackRecommendation, updateSessionProgress } from '../services/api';
import {
  trackInterviewStart,
  trackInterviewQuestionAnswer,
  trackInterviewComplete
} from '../services/analytics';

// Dynamic question generator based on context
const generateDynamicQuestions = (goal, previousAnswers) => {
  const goalLower = goal?.toLowerCase() || '';

  // Detect if this is an AI/software project (user is building a tool, not a physical product)
  const isAISoftwareProject = /\bai\b|artificial intelligence|chatbot|bot|app|software|platform|tool|assistant|system|automation|automate|dashboard|website|web app|mobile app|saas|api/.test(goalLower);

  // Detect goal categories for smarter questions - more specific patterns
  const isFinance = /stock|invest|trading|finance|money|budget|accounting|bank|crypto|portfolio/.test(goalLower);
  const isElectronics = /electronics|arduino|raspberry|circuit|pcb|microcontroller|sensor|iot|embedded/.test(goalLower);
  const isFurniture = /chair|table|desk|furniture|cabinet|shelf|shelving|woodwork|carpentry|bed|sofa|bench/.test(goalLower);
  const isClothing = /clothing|fashion|apparel|shirt|dress|pants|shoes|accessory|jewelry|hat|bag/.test(goalLower);
  const isCrafts = /craft|pottery|ceramic|textile|sewing|knitting|weaving|leather|handmade|artisan/.test(goalLower);
  const isDesign = /design|ui|ux|logo|brand|visual|graphic|website|app design/.test(goalLower);
  const isEcommerce = /shop|store|sell|ecommerce|product|inventory|marketplace|online business/.test(goalLower);
  const isFood = /restaurant|food|recipe|menu|cook|kitchen|bakery|cafe|catering|meal|wine|sommelier|beverage|cocktail|coffee|tea|culinary|chef|dining/.test(goalLower);
  const isResearch = /research|analyze|investigate|find|discover|study|academic|paper/.test(goalLower);
  const isAutomation = /automate|workflow|process|streamline|efficiency|schedule|reminder/.test(goalLower);
  const isEdgeAI = /jetson|nvidia|edge ai|embedded ai|detector|detection|recognition|camera|vision|tractor|pest|weed|insect|cockroach|ant|fly|flies/.test(goalLower);
  const isAgriculture = /farm|crop|field|tractor|agriculture|weed|harvest|plant|seed|irrigation|spray|greenhouse/.test(goalLower);
  const isRobotics = /robot|drone|autonomous|vehicle|navigation|motor|servo|actuator|gripper|arm robot/.test(goalLower);
  const isComputerVision = /detect|recognition|image|camera|vision|identify|classify|object detection|face/.test(goalLower);
  const isHealthcare = /health|medical|clinic|patient|wellness|fitness|therapy|doctor|nurse|hospital/.test(goalLower);
  const isEducation = /teach|learn|education|course|student|school|tutor|training|lesson/.test(goalLower);
  const isRealEstate = /property|real estate|house|apartment|rental|tenant|landlord|mortgage/.test(goalLower);
  const isMusic = /music|song|audio|sound|instrument|band|recording|podcast|beat/.test(goalLower);
  const isArt = /art|paint|draw|sculpture|gallery|exhibition|artist|illustration/.test(goalLower);
  const isWriting = /write|book|novel|blog|article|content|copywriting|story|script/.test(goalLower);
  const isConstruction = /construction|building|renovation|remodel|contractor|architecture|blueprint/.test(goalLower);
  const isAutomotive = /car|vehicle|automotive|mechanic|repair|garage|engine|tire/.test(goalLower);
  const isPets = /pet|dog|cat|animal|veterinary|grooming|breeding|shelter/.test(goalLower);
  const isTravel = /travel|trip|vacation|hotel|booking|tourism|flight|destination/.test(goalLower);
  const isLegal = /legal|lawyer|law|contract|compliance|attorney|lawsuit|patent/.test(goalLower);
  const isSports = /sport|fitness|gym|athlete|team|training|coach|game|competition/.test(goalLower);
  const isGaming = /game|gaming|esports|streamer|video game|mobile game/.test(goalLower);
  const isPhotography = /photo|photography|camera|portrait|wedding photo|studio/.test(goalLower);
  const isEvent = /event|party|wedding|conference|meetup|organize|planning/.test(goalLower);
  const isCleaning = /clean|cleaning|janitorial|maid|housekeeping|laundry/.test(goalLower);
  const isBeauty = /beauty|salon|spa|makeup|skincare|hair|nail|cosmetic/.test(goalLower);

  // Generic physical product (catch-all for build/make/create that aren't electronics or software)
  const isPhysicalProduct = /build|make|create|prototype|manufacture|produce/.test(goalLower) && !isElectronics && !isRobotics && !isAISoftwareProject;

  const questions = [];

  // Question 1: Project Scope/Ambition (always first)
  questions.push({
    id: 'projectScope',
    question: "What's your vision for this project?",
    subtitle: "This helps us recommend the right level of tools and infrastructure",
    type: 'cards',
    options: [
      {
        id: 'hobby',
        label: 'Personal Project',
        description: 'Learning, hobby, or personal use',
        icon: 'Lightbulb',
        color: 'purple'
      },
      {
        id: 'venture',
        label: 'New Venture',
        description: 'Starting a business or side hustle',
        icon: 'Rocket',
        color: 'blue'
      },
      {
        id: 'scale',
        label: 'Scale Production',
        description: 'Growing an existing operation',
        icon: 'Building2',
        color: 'green'
      },
      {
        id: 'moonshot',
        label: 'Moonshot',
        description: 'Ambitious, game-changing idea',
        icon: 'Flame',
        color: 'orange'
      },
      {
        id: 'enterprise',
        label: 'Enterprise',
        description: 'Internal feature or project estimation',
        icon: 'Briefcase',
        color: 'slate'
      }
    ]
  });

  // Question 2: Expertise (dynamic based on goal)
  const expertiseOptions = [];
  if (isFinance) expertiseOptions.push('Finance/Trading', 'Data Analysis', 'Business Strategy');
  if (isElectronics) expertiseOptions.push('Electronics/Hardware', 'Embedded Systems', 'Circuit Design');
  if (isFurniture) expertiseOptions.push('Woodworking/Carpentry', 'Furniture Design', 'Interior Design');
  if (isClothing) expertiseOptions.push('Fashion Design', 'Sewing/Tailoring', 'Textile Knowledge');
  if (isCrafts) expertiseOptions.push('Craftsmanship', 'Artisan Skills', 'Material Expertise');
  if (isDesign) expertiseOptions.push('Graphic Design', 'UI/UX Design', 'Brand Strategy');
  if (isEcommerce) expertiseOptions.push('E-commerce/Retail', 'Marketing', 'Supply Chain');
  if (isFood) expertiseOptions.push('Culinary Arts', 'Wine/Sommelier', 'Restaurant Operations', 'Food & Beverage', 'Food Science');
  if (isResearch) expertiseOptions.push('Data Science', 'Investigation/Research', 'Academic Research');
  if (isAutomation) expertiseOptions.push('Process Optimization', 'Systems Integration', 'Workflow Design');
  if (isEdgeAI || isRobotics) expertiseOptions.push('Robotics/Automation', 'Machine Learning/AI', 'Embedded Systems');
  if (isAgriculture) expertiseOptions.push('Agriculture/Farming', 'Agronomy', 'Farm Equipment');
  if (isComputerVision) expertiseOptions.push('Computer Vision', 'Image Processing', 'Machine Learning/AI');
  if (isHealthcare) expertiseOptions.push('Healthcare/Medical', 'Patient Care', 'Health Administration');
  if (isEducation) expertiseOptions.push('Teaching/Education', 'Curriculum Design', 'Training');
  if (isRealEstate) expertiseOptions.push('Real Estate', 'Property Management', 'Investment Analysis');
  if (isMusic) expertiseOptions.push('Music Production', 'Audio Engineering', 'Composition');
  if (isArt) expertiseOptions.push('Fine Arts', 'Illustration', 'Art Direction');
  if (isWriting) expertiseOptions.push('Writing/Copywriting', 'Content Strategy', 'Editing');
  if (isConstruction) expertiseOptions.push('Construction/Building', 'Architecture', 'Project Management');
  if (isAutomotive) expertiseOptions.push('Automotive/Mechanics', 'Vehicle Repair', 'Engineering');
  if (isPets) expertiseOptions.push('Animal Care', 'Veterinary Knowledge', 'Pet Training');
  if (isTravel) expertiseOptions.push('Travel/Tourism', 'Hospitality', 'Event Planning');
  if (isLegal) expertiseOptions.push('Legal/Law', 'Contract Management', 'Compliance');
  if (isSports) expertiseOptions.push('Sports/Fitness', 'Coaching', 'Athletic Training');
  if (isGaming) expertiseOptions.push('Game Development', 'Streaming/Content', 'Community Management');
  if (isPhotography) expertiseOptions.push('Photography', 'Photo Editing', 'Visual Storytelling');
  if (isEvent) expertiseOptions.push('Event Planning', 'Logistics', 'Vendor Management');
  if (isCleaning) expertiseOptions.push('Cleaning Services', 'Operations Management', 'Quality Control');
  if (isBeauty) expertiseOptions.push('Beauty/Cosmetics', 'Salon Operations', 'Skincare/Haircare');
  if (isPhysicalProduct) expertiseOptions.push('Product Design', 'Manufacturing', 'Prototyping');

  // Add common options only if we have few specific ones
  if (expertiseOptions.length < 3) {
    expertiseOptions.push('Business/Entrepreneurship', 'Marketing', 'Operations');
  }
  expertiseOptions.push('No specific expertise');

  // Remove duplicates
  const uniqueExpertise = [...new Set(expertiseOptions)];

  questions.push({
    id: 'expertise',
    question: previousAnswers.projectScope === 'moonshot'
      ? "What's your superpower? What expertise will drive this vision?"
      : "What's your background or expertise?",
    type: 'select',
    options: uniqueExpertise.slice(0, 8), // Max 8 options
    allowCustom: true
  });

  // Question 3: DIY Capabilities (dynamic based on goal and expertise)
  const capabilityOptions = [];

  if (isFurniture || previousAnswers.expertise?.includes('Woodworking') || previousAnswers.expertise?.includes('Carpentry')) {
    capabilityOptions.push('Woodworking / Carpentry', 'Furniture Assembly', 'Wood Finishing', 'Upholstery', 'Power Tool Operation');
  }
  if (isClothing || previousAnswers.expertise?.includes('Fashion') || previousAnswers.expertise?.includes('Sewing')) {
    capabilityOptions.push('Sewing / Tailoring', 'Pattern Making', 'Fabric Selection', 'Hand Stitching', 'Measurements');
  }
  if (isCrafts || previousAnswers.expertise?.includes('Crafts') || previousAnswers.expertise?.includes('Artisan')) {
    capabilityOptions.push('Hand Crafting', 'Material Preparation', 'Finishing / Detailing', 'Tool Handling');
  }
  if (isElectronics || previousAnswers.expertise?.includes('Electronics') || previousAnswers.expertise?.includes('Hardware')) {
    capabilityOptions.push('Electronics Assembly', 'Soldering / Wiring', 'PCB Design', '3D Printing', 'Sensor Integration');
  }
  if (isDesign || previousAnswers.expertise?.includes('Design')) {
    capabilityOptions.push('Graphic Design', 'Video Production', 'Photography', 'UI/UX Design');
  }
  if (isFood || previousAnswers.expertise?.includes('Culinary')) {
    capabilityOptions.push('Cooking / Food Prep', 'Recipe Development', 'Kitchen Management', 'Food Presentation');
  }
  if (isFinance || previousAnswers.expertise?.includes('Finance') || previousAnswers.expertise?.includes('Data')) {
    capabilityOptions.push('Financial Analysis', 'Spreadsheet Modeling', 'Report Writing', 'Bookkeeping');
  }
  if (isResearch || previousAnswers.expertise?.includes('Research') || previousAnswers.expertise?.includes('Investigation')) {
    capabilityOptions.push('Research / Investigation', 'Data Collection', 'Report Writing', 'Literature Review');
  }
  if (isEdgeAI || isRobotics || previousAnswers.expertise?.includes('Robotics') || previousAnswers.expertise?.includes('Embedded')) {
    capabilityOptions.push('Python / ML Programming', 'ROS / Robot Programming', 'Motor / Actuator Control', 'Sensor Calibration');
  }
  if (isAgriculture || previousAnswers.expertise?.includes('Agriculture') || previousAnswers.expertise?.includes('Farming')) {
    capabilityOptions.push('Tractor / Equipment Operation', 'Crop Identification', 'Field Mapping', 'Planting / Harvesting');
  }
  if (isComputerVision || previousAnswers.expertise?.includes('Vision') || previousAnswers.expertise?.includes('Image')) {
    capabilityOptions.push('Image Annotation', 'Model Training', 'Camera Setup', 'Dataset Collection');
  }
  if (isHealthcare || previousAnswers.expertise?.includes('Healthcare') || previousAnswers.expertise?.includes('Medical')) {
    capabilityOptions.push('Patient Care', 'Medical Records', 'Health Assessments', 'Scheduling');
  }
  if (isEducation || previousAnswers.expertise?.includes('Teaching') || previousAnswers.expertise?.includes('Education')) {
    capabilityOptions.push('Lesson Planning', 'Content Creation', 'Student Assessment', 'Tutoring');
  }
  if (isRealEstate || previousAnswers.expertise?.includes('Real Estate') || previousAnswers.expertise?.includes('Property')) {
    capabilityOptions.push('Property Tours', 'Market Analysis', 'Negotiation', 'Documentation');
  }
  if (isMusic || previousAnswers.expertise?.includes('Music') || previousAnswers.expertise?.includes('Audio')) {
    capabilityOptions.push('Music Composition', 'Audio Recording', 'Mixing / Mastering', 'Instrument Playing');
  }
  if (isArt || previousAnswers.expertise?.includes('Art') || previousAnswers.expertise?.includes('Illustration')) {
    capabilityOptions.push('Drawing / Painting', 'Digital Art', 'Sculpture', 'Art Curation');
  }
  if (isWriting || previousAnswers.expertise?.includes('Writing') || previousAnswers.expertise?.includes('Content')) {
    capabilityOptions.push('Writing / Copywriting', 'Editing / Proofreading', 'Research', 'Storytelling');
  }
  if (isConstruction || previousAnswers.expertise?.includes('Construction') || previousAnswers.expertise?.includes('Building')) {
    capabilityOptions.push('Construction Work', 'Blueprint Reading', 'Material Estimation', 'Site Management');
  }
  if (isAutomotive || previousAnswers.expertise?.includes('Automotive') || previousAnswers.expertise?.includes('Mechanic')) {
    capabilityOptions.push('Vehicle Repair', 'Diagnostics', 'Parts Sourcing', 'Maintenance');
  }
  if (isPets || previousAnswers.expertise?.includes('Animal') || previousAnswers.expertise?.includes('Pet')) {
    capabilityOptions.push('Animal Care', 'Pet Training', 'Grooming', 'Feeding / Nutrition');
  }
  if (isTravel || previousAnswers.expertise?.includes('Travel') || previousAnswers.expertise?.includes('Hospitality')) {
    capabilityOptions.push('Trip Planning', 'Booking Management', 'Customer Service', 'Local Knowledge');
  }
  if (isLegal || previousAnswers.expertise?.includes('Legal') || previousAnswers.expertise?.includes('Law')) {
    capabilityOptions.push('Legal Research', 'Document Drafting', 'Contract Review', 'Compliance Checks');
  }
  if (isSports || previousAnswers.expertise?.includes('Sports') || previousAnswers.expertise?.includes('Fitness')) {
    capabilityOptions.push('Coaching / Training', 'Performance Analysis', 'Workout Planning', 'Nutrition Planning');
  }
  if (isGaming || previousAnswers.expertise?.includes('Game') || previousAnswers.expertise?.includes('Streaming')) {
    capabilityOptions.push('Game Testing', 'Streaming / Recording', 'Community Management', 'Content Creation');
  }
  if (isPhotography || previousAnswers.expertise?.includes('Photography') || previousAnswers.expertise?.includes('Photo')) {
    capabilityOptions.push('Photography', 'Photo Editing', 'Lighting Setup', 'Equipment Handling');
  }
  if (isEvent || previousAnswers.expertise?.includes('Event') || previousAnswers.expertise?.includes('Planning')) {
    capabilityOptions.push('Event Coordination', 'Vendor Management', 'Guest Management', 'Decoration');
  }
  if (isCleaning || previousAnswers.expertise?.includes('Cleaning') || previousAnswers.expertise?.includes('Operations')) {
    capabilityOptions.push('Deep Cleaning', 'Equipment Operation', 'Quality Inspection', 'Scheduling');
  }
  if (isBeauty || previousAnswers.expertise?.includes('Beauty') || previousAnswers.expertise?.includes('Salon')) {
    capabilityOptions.push('Hair Styling', 'Makeup Application', 'Skincare Treatments', 'Client Consultation');
  }
  if (isPhysicalProduct) {
    capabilityOptions.push('Prototyping', 'Material Selection', 'Assembly', 'Quality Testing');
  }
  if (isAutomation) {
    capabilityOptions.push('Process Mapping', 'System Configuration', 'Testing / QA');
  }

  // Add common options only if we have few specific ones
  if (capabilityOptions.length < 4) {
    capabilityOptions.push('Project Management', 'Customer Service', 'Marketing / Sales');
  }
  capabilityOptions.push('None - Let AI handle everything');

  const uniqueCapabilities = [...new Set(capabilityOptions)];

  questions.push({
    id: 'userCapabilities',
    question: previousAnswers.projectScope === 'hobby'
      ? "What skills do you want to use or learn in this project?"
      : "What can you or your team do yourselves?",
    subtitle: "We'll figure out what you should DIY vs what AI should handle",
    type: 'multiselect',
    options: uniqueCapabilities.slice(0, 14),
    allowCustom: true,
    customPlaceholder: "Add other skills..."
  });

  // Question 4: Current Tools/Assets (dynamic based on goal)
  const toolOptions = [];

  if (isFurniture) {
    toolOptions.push('Woodworking Tools', 'Workshop / Garage', 'CAD Software', 'Power Tools', 'Hand Tools');
  }
  if (isClothing) {
    toolOptions.push('Sewing Machine', 'Pattern Software', 'Fabric Inventory', 'Mannequins', 'Cutting Tools');
  }
  if (isCrafts) {
    toolOptions.push('Craft Supplies', 'Work Space', 'Specialized Tools', 'Material Inventory');
  }
  if (isElectronics) {
    toolOptions.push('Soldering Station', 'Multimeter', 'Oscilloscope', 'Arduino / Raspberry Pi', '3D Printer');
  }
  if (isDesign) {
    toolOptions.push('Design Tools (Figma, Canva)', 'CAD Software', 'Adobe Creative Suite', 'Drawing Tablet');
  }
  if (isFinance) {
    toolOptions.push('Accounting Software', 'Excel / Spreadsheets', 'Trading Platform', 'Financial Data APIs');
  }
  if (isEcommerce) {
    toolOptions.push('Online Store Platform', 'POS System', 'Inventory Management', 'CRM Software');
  }
  if (isFood) {
    toolOptions.push('Commercial Kitchen', 'POS System', 'Inventory System', 'Recipe Management');
  }
  if (isResearch) {
    toolOptions.push('Research Database Access', 'Data Analysis Tools', 'Lab Equipment', 'Citation Manager');
  }
  if (isEdgeAI || isRobotics) {
    toolOptions.push('NVIDIA Jetson', 'Raspberry Pi', 'Arduino', 'Cameras / Sensors', 'Drones');
  }
  if (isAgriculture) {
    toolOptions.push('Tractor / Equipment', 'GPS / RTK System', 'Sprayer', 'Weather Station', 'Drones');
  }
  if (isComputerVision) {
    toolOptions.push('Cameras (USB/IP)', 'GPU Workstation', 'Training Datasets', 'Annotation Tools');
  }
  if (isHealthcare) {
    toolOptions.push('EHR System', 'Medical Equipment', 'Scheduling Software', 'Telehealth Platform');
  }
  if (isEducation) {
    toolOptions.push('LMS Platform', 'Video Conferencing', 'Course Materials', 'Assessment Tools');
  }
  if (isRealEstate) {
    toolOptions.push('MLS Access', 'CRM Software', 'Virtual Tour Equipment', 'Property Management Software');
  }
  if (isMusic) {
    toolOptions.push('DAW Software', 'Recording Equipment', 'Instruments', 'Mixing Console', 'Studio Space');
  }
  if (isArt) {
    toolOptions.push('Art Supplies', 'Digital Tools', 'Studio Space', 'Portfolio Website');
  }
  if (isWriting) {
    toolOptions.push('Writing Software', 'Research Tools', 'Publishing Platform', 'Grammar Tools');
  }
  if (isConstruction) {
    toolOptions.push('Construction Equipment', 'CAD/BIM Software', 'Project Management Tools', 'Safety Equipment');
  }
  if (isAutomotive) {
    toolOptions.push('Diagnostic Tools', 'Lift / Jack', 'Hand Tools', 'Parts Inventory', 'Shop Space');
  }
  if (isPets) {
    toolOptions.push('Grooming Equipment', 'Training Supplies', 'Booking System', 'Facility Space');
  }
  if (isTravel) {
    toolOptions.push('Booking Systems', 'CRM Software', 'Website / App', 'Supplier Contacts');
  }
  if (isLegal) {
    toolOptions.push('Legal Research Tools', 'Document Management', 'Case Management Software', 'E-signature Tools');
  }
  if (isSports) {
    toolOptions.push('Training Equipment', 'Performance Tracking', 'Video Analysis Tools', 'Facility Access');
  }
  if (isGaming) {
    toolOptions.push('Gaming Setup', 'Streaming Equipment', 'Development Tools', 'Community Platform');
  }
  if (isPhotography) {
    toolOptions.push('Camera Equipment', 'Lighting Gear', 'Editing Software', 'Studio Space');
  }
  if (isEvent) {
    toolOptions.push('Event Software', 'Vendor Contacts', 'Venue Partnerships', 'Decor Inventory');
  }
  if (isCleaning) {
    toolOptions.push('Cleaning Equipment', 'Supplies Inventory', 'Scheduling Software', 'Vehicle');
  }
  if (isBeauty) {
    toolOptions.push('Salon Equipment', 'Product Inventory', 'Booking System', 'Styling Tools');
  }
  if (isAutomation) {
    toolOptions.push('Workflow Tools (Zapier, Make)', 'CRM', 'Communication Tools');
  }
  if (isPhysicalProduct) {
    toolOptions.push('Prototyping Tools', 'Manufacturing Equipment', 'CAD Software', 'Material Inventory');
  }

  // Add common fallback options
  if (toolOptions.length < 3) {
    toolOptions.push('Computer / Laptop', 'Smartphone', 'Basic Office Setup');
  }
  toolOptions.push('None / Starting fresh');

  const uniqueTools = [...new Set(toolOptions)];

  questions.push({
    id: 'currentTools',
    question: previousAnswers.projectScope === 'scale'
      ? "What tools and systems are you already using?"
      : "Do you have any existing tools or assets we should integrate?",
    type: 'multiselect',
    options: uniqueTools.slice(0, 10),
    allowCustom: true
  });

  // Question 5: Budget (dynamic based on project scope)
  const budgetQuestion = {
    id: 'budget',
    type: 'select'
  };

  if (previousAnswers.projectScope === 'enterprise') {
    budgetQuestion.question = "What's the estimated budget range for this project?";
    budgetQuestion.options = [
      'Under $10K',
      '$10K - $50K',
      '$50K - $200K',
      '$200K+',
      'Budget TBD - need estimation first'
    ];
  } else if (previousAnswers.projectScope === 'hobby') {
    budgetQuestion.question = "How much are you comfortable spending monthly?";
    budgetQuestion.options = [
      'Free only ($0)',
      'Coffee money ($1-20/month)',
      'Hobby budget ($20-50/month)',
      'Invested ($50-100/month)'
    ];
  } else if (previousAnswers.projectScope === 'moonshot') {
    budgetQuestion.question = "What's your monthly tool budget for this mission?";
    budgetQuestion.options = [
      'Bootstrap ($0-50/month)',
      'Seed stage ($50-200/month)',
      'Growth stage ($200-500/month)',
      'Scale stage ($500+/month)',
      'Budget TBD - focus on best solution'
    ];
  } else if (previousAnswers.projectScope === 'scale') {
    budgetQuestion.question = "What's your monthly budget for tools and infrastructure?";
    budgetQuestion.options = [
      'Lean ($50-100/month)',
      'Growing ($100-300/month)',
      'Scaling ($300-500/month)',
      'Enterprise ($500+/month)'
    ];
  } else {
    budgetQuestion.question = "What's your monthly budget for tools and APIs?";
    budgetQuestion.options = [
      'Free only ($0)',
      'Starter ($1-50/month)',
      'Growth ($50-200/month)',
      'Scale ($200+/month)',
      'Not sure yet'
    ];
  }

  questions.push(budgetQuestion);

  // Question 6: Timeline (dynamic based on project scope)
  const timelineQuestion = {
    id: 'timeline',
    type: 'select'
  };

  if (previousAnswers.projectScope === 'enterprise') {
    timelineQuestion.question = "What's the expected project timeline?";
    timelineQuestion.options = [
      'Quick win (1-4 weeks)',
      'Quarter project (1-3 months)',
      'Multi-quarter (3-6 months)',
      'Annual initiative (6-12 months)',
      'Timeline TBD - need estimation first'
    ];
  } else if (previousAnswers.projectScope === 'moonshot') {
    timelineQuestion.question = "What's your timeline to first milestone?";
    timelineQuestion.options = [
      'Sprint mode (within days)',
      'MVP in weeks',
      'Building foundation (1-3 months)',
      'Long-term vision (6+ months)'
    ];
  } else if (previousAnswers.projectScope === 'hobby') {
    timelineQuestion.question = "How soon do you want to get started?";
    timelineQuestion.options = [
      'This weekend!',
      'Within a week or two',
      'No rush - learning as I go',
      'Just exploring ideas'
    ];
  } else {
    timelineQuestion.question = "When do you need this working?";
    timelineQuestion.options = [
      'ASAP (within days)',
      'Soon (1-2 weeks)',
      'Planning ahead (1-3 months)',
      'Just exploring'
    ];
  }

  questions.push(timelineQuestion);

  // Question 7: Additional Context (always last, but personalized)
  const contextQuestion = {
    id: 'additionalContext',
    type: 'text'
  };

  if (previousAnswers.projectScope === 'enterprise') {
    contextQuestion.question = "Describe the feature or project you need to estimate.";
    contextQuestion.placeholder = "What's the business goal? Any technical constraints, existing systems to integrate with, or team size considerations?";
  } else if (previousAnswers.projectScope === 'moonshot') {
    contextQuestion.question = "Tell us more about your vision. What makes this idea special?";
    contextQuestion.placeholder = "Share the big picture - what problem are you solving? What's your unique angle?";
  } else if (previousAnswers.projectScope === 'scale') {
    contextQuestion.question = "What are your current bottlenecks or challenges?";
    contextQuestion.placeholder = "What's slowing you down? What would help you scale faster?";
  } else if (previousAnswers.projectScope === 'venture') {
    contextQuestion.question = "Anything else that would help us recommend the perfect stack?";
    contextQuestion.placeholder = "Target market, competitors, unique requirements...";
  } else {
    contextQuestion.question = "Anything else we should know?";
    contextQuestion.placeholder = "Optional: Any specific requirements or constraints...";
  }

  questions.push(contextQuestion);

  return questions;
};

// Icon components mapping
const IconMap = {
  Lightbulb,
  Rocket,
  Building2,
  Flame,
  Briefcase
};

export default function Configure() {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const {
    goal,
    sessionId,
    interview,
    messages,
    updateInterview,
    addMessage,
    setStack,
    setCurrentStep,
    isLoading,
    setLoading,
    setError
  } = useConfigStore();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const initRef = useRef(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  // Da Vinci inventions for loading messages
  const vinciLoadingMessages = [
    { text: "Sketching your flying machine", emoji: "🚁" },
    { text: "Calibrating the aerial screw", emoji: "⚙️" },
    { text: "Mixing the perfect pigments", emoji: "🎨" },
    { text: "Consulting the Vitruvian Man", emoji: "🧍" },
    { text: "Winding the mechanical knight", emoji: "🤖" },
    { text: "Studying bird wing patterns", emoji: "🦅" },
    { text: "Drafting with mirror script", emoji: "✍️" },
    { text: "Calculating golden ratios", emoji: "📐" },
    { text: "Engineering the tank design", emoji: "🛡️" },
    { text: "Perfecting the parachute", emoji: "🪂" },
    { text: "Designing hydraulic systems", emoji: "💧" },
    { text: "Assembling the self-propelled cart", emoji: "🚗" },
  ];

  // Generate dynamic questions based on goal and previous answers
  const dynamicQuestions = useMemo(() => {
    return generateDynamicQuestions(goal, interview);
  }, [goal, interview]);

  // Redirect if no goal
  useEffect(() => {
    if (!goal) {
      navigate('/home');
    }
  }, [goal, navigate]);

  // Add initial messages (only once)
  useEffect(() => {
    if (initRef.current || !goal) return;

    if (messages.length === 0) {
      initRef.current = true;
      trackInterviewStart(goal);
      addMessage({
        role: 'assistant',
        content: `Great! You want to "${goal}". Let me ask a few smart questions to configure the perfect AI + tools stack for you.`
      });

      setTimeout(() => {
        const firstQuestion = dynamicQuestions[0];
        const questionText = firstQuestion.subtitle
          ? `${firstQuestion.question}\n\n${firstQuestion.subtitle}`
          : firstQuestion.question;
        addMessage({
          role: 'assistant',
          content: questionText
        });
        setInitialized(true);
      }, 800);
    } else {
      initRef.current = true;
      setInitialized(true);
    }
  }, [goal, messages.length, addMessage, dynamicQuestions]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Cycle through loading messages when loading
  useEffect(() => {
    if (!isLoading) {
      setLoadingMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % vinciLoadingMessages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [isLoading, vinciLoadingMessages.length]);

  const currentQuestion = dynamicQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / dynamicQuestions.length) * 100;

  const handleAnswer = async (answer) => {
    // Add user message
    const displayAnswer = Array.isArray(answer) ? answer.join(', ') :
      (typeof answer === 'object' ? answer.label : answer);
    addMessage({ role: 'user', content: displayAnswer });

    // Update interview data
    // Handle arrays (multiselect), objects with id (select options), and strings
    const answerValue = Array.isArray(answer) ? answer :
      (typeof answer === 'object' && answer !== null ? answer.id : answer);
    updateInterview({ [currentQuestion.id]: answerValue });

    // Track question answer
    trackInterviewQuestionAnswer(
      currentQuestion.id,
      currentQuestionIndex,
      dynamicQuestions.length
    );

    // Update session progress in Firestore (non-blocking)
    const updatedProgress = { ...interview, [currentQuestion.id]: answerValue };
    updateSessionProgress(sessionId, {
      currentStep: 'interview',
      interviewProgress: updatedProgress
    }).catch(console.error);

    // Move to next question or finish
    if (currentQuestionIndex < dynamicQuestions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);

      // Re-generate questions with updated answers for next question
      const updatedInterview = { ...interview, [currentQuestion.id]: answerValue };
      const updatedQuestions = generateDynamicQuestions(goal, updatedInterview);
      const nextQuestion = updatedQuestions[nextIndex];

      // Add next question
      setTimeout(() => {
        const questionText = nextQuestion.subtitle
          ? `${nextQuestion.question}\n\n${nextQuestion.subtitle}`
          : nextQuestion.question;
        addMessage({
          role: 'assistant',
          content: questionText
        });
      }, 500);
    } else {
      // Interview complete - generate stack
      trackInterviewComplete(goal, dynamicQuestions.length);
      setTimeout(async () => {
        addMessage({
          role: 'assistant',
          content: "Perfect! I have everything I need. Let me configure your ideal stack..."
        });
        setLoading(true);

        try {
          const finalAnswerValue = typeof answer === 'object' ? answer.id : answer;
          // Call Claude to generate stack recommendation
          const result = await generateStackRecommendation({
            sessionId,
            goal,
            projectScope: interview.projectScope || '',
            expertise: interview.expertise || '',
            userCapabilities: interview.userCapabilities || [],
            currentTools: interview.currentTools || [],
            budget: interview.budget || '',
            timeline: interview.timeline || '',
            additionalContext: currentQuestion.id === 'additionalContext' ? finalAnswerValue : interview.additionalContext
          });

          setStack(result);
          setCurrentStep('stack');
          navigate('/stack');
        } catch (error) {
          console.error('Stack generation error:', error);
          setError(error.message);
          addMessage({
            role: 'assistant',
            content: "I encountered an error generating your stack. Please try again."
          });
        } finally {
          setLoading(false);
        }
      }, 500);
    }

    // Reset inputs
    setInputValue('');
    setSelectedOptions([]);
  };

  const handleSelectOption = (option) => {
    if (currentQuestion.type === 'multiselect') {
      setSelectedOptions(prev =>
        prev.includes(option)
          ? prev.filter(o => o !== option)
          : [...prev, option]
      );
    } else {
      handleAnswer(option);
    }
  };

  const handleCardSelect = (option) => {
    handleAnswer(option);
  };

  const handleSubmitMultiselect = () => {
    if (selectedOptions.length > 0) {
      handleAnswer(selectedOptions);
    }
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() || currentQuestion.type === 'text') {
      handleAnswer(inputValue.trim() || 'No additional context');
    }
  };

  if (!goal) return null;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded flex items-center justify-center">
              <GoldenSpiral className="w-4 h-4" color="white" />
            </div>
            <span className="font-medium">VinciKit</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-gray-100">
        <motion.div
          className="h-full bg-blue-600"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Usage Banner */}
      <div className="px-6 pt-4">
        <div className="max-w-2xl mx-auto">
          <UsageBanner />
        </div>
      </div>

      {/* Chat area */}
      <main className="flex-1 overflow-y-auto px-6 py-8">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Goal display */}
          <div className="text-center mb-8">
            <p className="text-sm text-gray-500">Your goal</p>
            <p className="font-medium text-lg text-gray-900">"{goal}"</p>
          </div>

          {/* Messages */}
          <AnimatePresence>
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl whitespace-pre-line ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md'
                  }`}
                >
                  {message.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Loading indicator with Da Vinci inventions */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-3">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <AnimatePresence mode="wait">
                  <motion.span
                    key={loadingMessageIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="text-gray-600 flex items-center gap-2"
                  >
                    <span>{vinciLoadingMessages[loadingMessageIndex].emoji}</span>
                    <span>{vinciLoadingMessages[loadingMessageIndex].text}...</span>
                  </motion.span>
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input area */}
      {!isLoading && currentQuestion && initialized && (
        <div className="border-t border-gray-200 bg-white px-6 py-4">
          <div className="max-w-2xl mx-auto">
            {/* Card selection for project scope */}
            {currentQuestion.type === 'cards' && (
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-3">
                  {currentQuestion.options.map((option) => {
                    const IconComponent = IconMap[option.icon];
                    const colorClasses = {
                      purple: 'border-purple-200 hover:border-purple-400 hover:bg-purple-50',
                      blue: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50',
                      green: 'border-green-200 hover:border-green-400 hover:bg-green-50',
                      orange: 'border-orange-200 hover:border-orange-400 hover:bg-orange-50',
                      slate: 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                    };
                    const iconColors = {
                      purple: 'text-purple-600 bg-purple-100',
                      blue: 'text-blue-600 bg-blue-100',
                      green: 'text-green-600 bg-green-100',
                      orange: 'text-orange-600 bg-orange-100',
                      slate: 'text-slate-600 bg-slate-100'
                    };
                    const isEnterprise = option.id === 'enterprise';
                    return (
                      <button
                        key={option.id}
                        onClick={() => handleCardSelect(option)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${colorClasses[option.color]} bg-white ${isEnterprise ? 'col-span-2' : ''}`}
                      >
                        <div className={`${isEnterprise ? 'flex items-center gap-4' : ''}`}>
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isEnterprise ? '' : 'mb-3'} ${iconColors[option.color]}`}>
                            {IconComponent && <IconComponent className="w-5 h-5" />}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{option.label}</p>
                            <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Options for select/multiselect */}
            {(currentQuestion.type === 'select' || currentQuestion.type === 'multiselect') && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {currentQuestion.options.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleSelectOption(option)}
                      className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                        currentQuestion.type === 'multiselect' && selectedOptions.includes(option)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white border-gray-200 hover:border-blue-400 hover:text-blue-600'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                {currentQuestion.type === 'multiselect' && selectedOptions.length > 0 && (
                  <button
                    onClick={handleSubmitMultiselect}
                    className="mt-3 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Continue with {selectedOptions.length} selected
                  </button>
                )}
              </div>
            )}

            {/* Text input */}
            {(currentQuestion.type === 'text' || currentQuestion.allowCustom) && (
              <form onSubmit={handleTextSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={currentQuestion.placeholder || currentQuestion.customPlaceholder || "Type your answer..."}
                  className="flex-1 px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}

            {/* Skip option for optional questions */}
            {currentQuestion.type === 'text' && (
              <button
                onClick={() => handleAnswer('No additional context')}
                className="mt-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Skip this question
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
